import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { styled as styledMUI } from '@mui/material/styles';
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE } from '../../shared/Constants';

type AccountAction = 'password' | 'conversations' | 'account';
type Feedback = { type: 'success' | 'error'; message: string } | null;

export interface AccountModalProps {
  openModal: boolean;
  setOpenModal: (open: boolean) => void;
  onClearConversations: () => Promise<boolean>;
}

function AccountModal({ openModal, setOpenModal, onClearConversations }: AccountModalProps) {
  const { logout, getAccessTokenSilently, user } = useAuth0();
  const [currentAction, setCurrentAction] = useState<AccountAction | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  useEffect(() => {
    if (openModal) {
      setCurrentAction(null);
      setFeedback(null);
    }
  }, [openModal]);

  const handlePasswordChange = async () => {
    const domain = process.env.REACT_APP_AUTH_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID;
    setFeedback(null);

    if (!domain || !clientId || !user?.email) {
      setFeedback({ type: 'error', message: 'Não foi possível solicitar a alteração de senha.' });
      return;
    }

    setCurrentAction('password');
    try {
      const response = await fetch(`https://${domain}/dbconnections/change_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          email: user.email,
          connection: 'Username-Password-Authentication',
        }),
      });
      if (!response.ok) throw new Error('Password reset request failed');
      setFeedback({ type: 'success', message: 'Enviamos um e-mail para você alterar sua senha.' });
    } catch {
      setFeedback({ type: 'error', message: 'Não foi possível solicitar a alteração de senha.' });
    } finally {
      setCurrentAction(null);
    }
  };

  const handleClearConversations = async () => {
    setFeedback(null);
    setCurrentAction('conversations');
    try {
      const cleared = await onClearConversations();
      if (cleared) {
        setFeedback({ type: 'success', message: 'Todas as conversas foram excluídas.' });
      }
    } catch {
      setFeedback({ type: 'error', message: 'Não foi possível limpar as conversas agora.' });
    } finally {
      setCurrentAction(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Você tem certeza que deseja apagar sua conta? Esta ação é irreversível.')) {
      return;
    }

    setFeedback(null);
    setCurrentAction('account');
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(API_BASE + '/api/delete-account', {
        method: 'DELETE',
        headers: {
          Authorization: `Auth ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Account deletion failed');
      logout({ logoutParams: { returnTo: window.location.origin + '/auth' } });
    } catch {
      setFeedback({ type: 'error', message: 'Erro ao apagar conta. Tente novamente mais tarde.' });
    } finally {
      setCurrentAction(null);
    }
  };

  const isBusy = currentAction !== null;

  return (
    <StyledModal open={openModal} onClose={() => setOpenModal(false)}>
      <ModalContainer role="dialog" aria-modal="true" aria-labelledby="account-modal-title">
        <Header>
          <div>
            <Title id="account-modal-title">Conta</Title>
            <Subtitle>Gerencie sua senha e seus dados.</Subtitle>
          </div>
          <CloseButton aria-label="Fechar" disabled={isBusy} onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </CloseButton>
        </Header>
        <Content>
          {feedback && (
            <FeedbackMessage
              $type={feedback.type}
              role={feedback.type === 'error' ? 'alert' : 'status'}
            >
              {feedback.message}
            </FeedbackMessage>
          )}
          <ActionButton aria-label="Alterar senha" disabled={isBusy} onClick={handlePasswordChange}>
            <ActionIcon><LockResetOutlinedIcon /></ActionIcon>
            <ActionCopy>
              <ActionTitle>{currentAction === 'password' ? 'Solicitando…' : 'Alterar senha'}</ActionTitle>
              <ActionDescription>Receba por e-mail um link seguro para definir uma nova senha.</ActionDescription>
            </ActionCopy>
          </ActionButton>
          <ActionButton
            aria-label="Excluir todas as conversas"
            disabled={isBusy}
            onClick={handleClearConversations}
          >
            <ActionIcon><DeleteSweepOutlinedIcon /></ActionIcon>
            <ActionCopy>
              <ActionTitle>
                {currentAction === 'conversations' ? 'Excluindo…' : 'Excluir todas as conversas'}
              </ActionTitle>
              <ActionDescription>Apague permanentemente todo o histórico de conversas.</ActionDescription>
            </ActionCopy>
          </ActionButton>
          <ActionButton $danger aria-label="Apagar conta" disabled={isBusy} onClick={handleDeleteAccount}>
            <ActionIcon><PersonRemoveOutlinedIcon /></ActionIcon>
            <ActionCopy>
              <ActionTitle>{currentAction === 'account' ? 'Apagando…' : 'Apagar conta'}</ActionTitle>
              <ActionDescription>Exclua permanentemente sua conta, chaves, conversas e créditos.</ActionDescription>
            </ActionCopy>
          </ActionButton>
        </Content>
      </ModalContainer>
    </StyledModal>
  );
}

export default AccountModal;

const StyledModal = styledMUI(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const ModalContainer = styled.div`
  font-family: var(--font-secondary);
  background: ${({ theme }) => theme.modal_background};
  color: ${({ theme }) => theme.font_color};
  border: 1px solid ${({ theme }) => theme.modal_border};
  width: min(560px, 100%);
  max-height: min(720px, calc(100vh - 32px));
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  outline: none;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.18);
  overflow: hidden;

  @media (max-width: 600px) {
    max-height: calc(100vh - 20px);
    border-radius: 14px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  padding: 28px 32px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.modal_border};
  flex-shrink: 0;

  @media (max-width: 600px) {
    padding: 20px 20px 16px;
  }
`;

const Title = styled.h1`
  font: 700 1.65rem/1.2 var(--font-primary);
  margin: 0;
  color: ${({ theme }) => theme.font_color};
`;

const Subtitle = styled.p`
  margin: 6px 0 0;
  color: ${({ theme }) => theme.text_secondary || '#666'};
  font: 400 0.95rem/1.4 var(--font-primary);
`;

const CloseButton = styled.button`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.arrow_icon_color};
  padding: 7px;
  border-radius: 50%;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hover_overlay_light};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Content = styled.div`
  padding: 24px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;

  @media (max-width: 600px) {
    padding: 18px 20px 22px;
  }
`;

const FeedbackMessage = styled.div<{ $type: 'success' | 'error' }>`
  padding: 11px 13px;
  border-radius: 8px;
  font: 500 0.9rem/1.4 var(--font-primary);
  color: ${({ $type }) => ($type === 'success' ? '#166534' : '#991b1b')};
  background: ${({ $type }) => (
    $type === 'success' ? 'rgba(22, 101, 52, 0.09)' : 'rgba(153, 27, 27, 0.08)'
  )};
  border: 1px solid ${({ $type }) => (
    $type === 'success' ? 'rgba(22, 101, 52, 0.25)' : 'rgba(153, 27, 27, 0.25)'
  )};
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 16px;
  text-align: left;
  color: ${({ theme, $danger }) => ($danger ? '#991b1b' : theme.font_color)};
  background: transparent;
  border: 1px solid ${({ theme, $danger }) => (
    $danger ? 'rgba(153, 27, 27, 0.45)' : theme.modal_border
  )};
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme, $danger }) => (
      $danger ? 'rgba(153, 27, 27, 0.05)' : theme.hover_overlay_light
    )};
    border-color: ${({ theme, $danger }) => (
      $danger ? '#991b1b' : theme.arrow_icon_color
    )};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ActionIcon = styled.span`
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  margin-top: 1px;

  svg {
    font-size: 1.45rem;
  }
`;

const ActionCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ActionTitle = styled.span`
  font: 600 1rem/1.3 var(--font-primary);
`;

const ActionDescription = styled.span`
  color: ${({ theme }) => theme.text_secondary || '#666'};
  font: 400 0.9rem/1.45 var(--font-primary);
`;
